import React from "react";
import { AlertTriangle, User } from "lucide-react";
import { fmtDate, formatInGymTimezone } from "@/lib/utils-app";

function paymentLabel(display) {
  const days = display?.days_until_payment;
  if (days === null || days === undefined) return "Sin pago mensual registrado";
  if (days < 0) return `Pago vencido · ${Math.abs(days)} día${Math.abs(days) === 1 ? "" : "s"}`;
  if (days === 0) return "Próximo pago · hoy";
  return `Próximo pago en ${days} día${days === 1 ? "" : "s"}`;
}

function fmtClock(iso, gymTz) {
  if (!iso) return "—";
  return formatInGymTimezone(iso, gymTz, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

export default function AttendanceSuccessOverlay({ display, gymTz, onDismiss }) {
  if (!display) return null;

  const isEarlyRejected = display.action === "early_rejected";
  const isAlreadyToday = display.action === "already_today";
  const isPaymentAlert = !isEarlyRejected && !isAlreadyToday && !!display.payment_alert;
  const title = isEarlyRejected
    ? "⚠ Llegada anticipada"
    : isAlreadyToday
      ? "ℹ Ya registró asistencia hoy"
      : isPaymentAlert
        ? "⚠ Asistencia — pago pendiente"
        : "✓ Asistencia registrada";

  const rows = [
    { label: "Paquete", value: display.package_name || "—" },
    {
      label: "Fecha de inscripción",
      value: display.enrolled_date_label || fmtDate(display.enrolled_at),
    },
    {
      label: "Días desde el registro",
      value:
        display.days_since_enrollment != null
          ? `${display.days_since_enrollment} día${display.days_since_enrollment === 1 ? "" : "s"}`
          : "—",
    },
    { label: "Próximo pago", value: paymentLabel(display) },
    { label: "Turno", value: display.shift_window || display.shift_name || "—" },
  ];

  if (isEarlyRejected) {
    rows.push({ label: "Hora del intento", value: fmtClock(display.check_in_at, gymTz) });
    rows.push({
      label: "Puede entrar desde",
      value: display.earliest_entry_label || "—",
      highlight: true,
    });
    if (display.billing_comment) {
      rows.push({ label: "Nota", value: display.billing_comment, highlight: true });
    }
  } else {
    rows.push({ label: "Hora de asistencia", value: fmtClock(display.check_in_at, gymTz) });
  }

  return (
    <div
      className={`fixed inset-0 z-[200] h-[100dvh] max-h-[100dvh] w-full flex flex-col overflow-hidden ${
        isPaymentAlert
          ? "bg-[#1a0808]"
          : isEarlyRejected
            ? "bg-[#1a1208]"
            : isAlreadyToday
              ? "bg-[#081018]"
              : "bg-[#050505]"
      }`}
      role="dialog"
      aria-labelledby="attendance-success-title"
      onClick={onDismiss}
    >
      <div className="flex-1 min-h-0 flex flex-col items-center justify-center px-3 sm:px-6 py-2 w-full max-w-6xl mx-auto">
        <p
          className={`shrink-0 text-[clamp(0.75rem,2.2vh,1.25rem)] uppercase tracking-[0.25em] font-medium mb-[1vh] ${
            isPaymentAlert
              ? "text-red-400"
              : isEarlyRejected
                ? "text-amber-400"
                : isAlreadyToday
                  ? "text-sky-400"
                  : "text-green-400"
          }`}
        >
          {title}
        </p>

        {isEarlyRejected && (
          <div className="shrink-0 w-full max-w-4xl mb-[1.5vh] flex items-center justify-center gap-3 px-4 py-3 rounded-sm border-2 border-amber-500 bg-amber-950/80">
            <AlertTriangle className="text-amber-400 shrink-0" size={32} />
            <div className="text-left">
              <p className="text-amber-300 font-bold text-[clamp(1rem,3vh,1.75rem)] uppercase">
                Aún no cuenta como entrada
              </p>
              <p className="text-amber-200/90 text-[clamp(0.85rem,2.2vh,1.25rem)]">
                {display.billing_comment || "Llegada anticipada — espere su horario"}
              </p>
            </div>
          </div>
        )}

        {isAlreadyToday && (
          <div className="shrink-0 w-full max-w-4xl mb-[1.5vh] flex items-center justify-center gap-3 px-4 py-3 rounded-sm border-2 border-sky-500 bg-sky-950/80">
            <AlertTriangle className="text-sky-400 shrink-0" size={32} />
            <div className="text-left">
              <p className="text-sky-300 font-bold text-[clamp(1rem,3vh,1.75rem)] uppercase">
                Asistencia ya registrada
              </p>
              <p className="text-sky-200/90 text-[clamp(0.85rem,2.2vh,1.25rem)]">
                {display.message || "Ya registró asistencia hoy. No se creó un nuevo registro."}
              </p>
            </div>
          </div>
        )}

        {isPaymentAlert && (
          <div className="shrink-0 w-full max-w-4xl mb-[1.5vh] flex items-center justify-center gap-3 px-4 py-3 rounded-sm border-2 border-red-500 bg-red-950/80 animate-pulse">
            <AlertTriangle className="text-red-400 shrink-0" size={32} />
            <div className="text-left">
              <p className="text-red-300 font-bold text-[clamp(1rem,3vh,1.75rem)] uppercase">
                Cobrar en recepción
              </p>
              <p className="text-red-200/90 text-[clamp(0.85rem,2.2vh,1.25rem)] capitalize">
                {display.payment_alert_message || "Inscripción y/o mensualidad sin pagar"}
              </p>
            </div>
          </div>
        )}

        <div
          className={`shrink-0 w-[clamp(5rem,18vh,11rem)] h-[clamp(5rem,18vh,11rem)] rounded-full border-[3px] overflow-hidden bg-white/10 ${
            isPaymentAlert
              ? "border-red-500 shadow-[0_0_30px_rgba(239,68,68,0.45)]"
              : isEarlyRejected
                ? "border-amber-500 shadow-[0_0_30px_rgba(245,158,11,0.35)]"
                : isAlreadyToday
                  ? "border-sky-500 shadow-[0_0_30px_rgba(14,165,233,0.35)]"
                  : "border-green-500 shadow-[0_0_30px_rgba(34,197,94,0.35)]"
          }`}
        >
          {display.profile_photo ? (
            <img src={display.profile_photo} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/40">
              <User className="w-[55%] h-[55%]" strokeWidth={1} />
            </div>
          )}
        </div>

        <h2
          id="attendance-success-title"
          className="shrink-0 font-display text-[clamp(1.5rem,6.5vh,4.5rem)] text-[#FFD700] mt-[1.2vh] leading-tight uppercase text-center px-2"
        >
          {display.full_name}
        </h2>

        <dl className="flex-1 min-h-0 w-full max-w-4xl flex flex-col justify-center gap-[0.6vh] my-[0.8vh] overflow-hidden">
          {rows.map((row) => (
            <div
              key={row.label}
              className="shrink-0 grid grid-cols-[minmax(0,42%)_1fr] gap-x-3 sm:gap-x-6 items-baseline border-b border-white/10 pb-[0.5vh]"
            >
              <dt className="text-[clamp(0.6rem,1.8vh,1rem)] uppercase tracking-widest text-white/40 leading-tight">
                {row.label}
              </dt>
              <dd
                className={`text-[clamp(0.85rem,2.8vh,2rem)] font-semibold leading-tight ${
                  row.highlight ? "text-amber-400" : "text-white"
                }`}
              >
                {row.value}
              </dd>
            </div>
          ))}
        </dl>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          className="shrink-0 mt-[1vh] btn-primary text-[clamp(0.85rem,2vh,1.1rem)] px-6 py-2 sm:py-3"
        >
          Continuar
        </button>
      </div>
    </div>
  );
}
